import type { Interval, IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"

/**
 * Accounting-specific All-Time Helpers
 *
 * These helpers require GraphQL types (Interval, IntervalOrigin) and dayjs.
 * Pure URL helpers live in @digits-shared/helpers/allTimeHelpers.
 */

/**
 * Computes the intervalCount for an all-time range.
 * Counts the number of intervals from firstTransactionTimestamp to endTimestamp.
 */
export function computeAllTimeIntervalCount(
  interval: Interval,
  firstTransactionTimestamp: number,
  endTimestamp: number
): number {
  const firstDate = dateTimeHelper.utcDayjsFromTimestamp(firstTransactionTimestamp)
  const endDate = dateTimeHelper.utcDayjsFromTimestamp(endTimestamp)
  const unit = dateTimeHelper.unitOfTimeForInterval(interval)
  const diff = endDate.startOf(unit).diff(firstDate.startOf(unit), unit)
  return Math.abs(diff) + 1
}

/**
 * Creates an all-time IntervalOrigin anchored at endTimestamp spanning back to firstTransactionTimestamp.
 * Returns undefined if no firstTransactionTimestamp is available.
 */
export function createAllTimeOrigin(
  interval: Interval,
  firstTransactionTimestamp: number | undefined,
  endTimestamp: number
): IntervalOrigin | undefined {
  if (!firstTransactionTimestamp) {
    return undefined
  }

  const anchorOrigin = dateTimeHelper.intervalOriginFromPeriod(
    dateTimeHelper.periodFromTimestamp(endTimestamp, interval)
  )

  const intervalCount = computeAllTimeIntervalCount(
    interval,
    firstTransactionTimestamp,
    endTimestamp
  )

  return {
    ...anchorOrigin,
    intervalCount,
  }
}

/**
 * Checks if the passed time params match the context origin.
 * This is used to determine if params are "explicit" (user selected)
 * or just the current context being passed through.
 */
export function paramsMatchOrigin(
  params: { year?: string | number; index?: string | number },
  origin: IntervalOrigin | undefined
): boolean {
  if (!origin || params.year === undefined) {
    return false
  }
  return (
    String(params.year) === String(origin.year) && String(params.index) === String(origin.index)
  )
}
